import type { ComponentProps, RefObject, JSX } from 'react'

import type { MentionContextType } from '../../graphqlEnums'

import type { UseMentionReplyFormReturn } from './hooks/useMentionReplyForm'
import { EngagementReplyForm } from '../../components/EngagementReplyForm'
import { useMentionData } from '../context/MentionContext'
import { MentionReplyFormHeader } from '../MentionsList/MentionReplyFormHeader'

import styles from './MentionReplyForm.module.css'

/**
 * Placeholder per mention kind. A post mention means the user will leave a
 * comment on the source post (`Type your comment...`); a comment mention means
 * the user will reply inside the existing comment thread (`Type your
 * reply...`). Falls back to the reply copy for any future `MentionContextType`
 * the matrix doesn't cover explicitly.
 */
const PLACEHOLDER_BY_MENTION_TYPE: Partial<Record<MentionContextType, string>> =
  {
    post: 'Type your comment...',
    comment: 'Type your reply...',
  }

const DEFAULT_PLACEHOLDER = 'Type your reply...'

type MentionReplyFormProps = {
  channel: ComponentProps<typeof EngagementReplyForm>['channel']
  reply: UseMentionReplyFormReturn
  textareaRef: RefObject<HTMLTextAreaElement>
  expanded: boolean
  canReply: boolean
}

/**
 * Mention reply area. Reads mention context and feeds the shared
 * `EngagementReplyForm` the mention-specific slots — the suggestions header,
 * aria label, per-kind placeholder, assets, analytics CTA, and the
 * processing-row test id. The shared component owns the processing-vs-form
 * gating; reply state lives in the parent's `useMentionReplyForm`.
 */
export const MentionReplyForm = ({
  channel,
  reply,
  textareaRef,
  expanded,
  canReply,
}: MentionReplyFormProps): JSX.Element => {
  const mention = useMentionData()
  const placeholder =
    PLACEHOLDER_BY_MENTION_TYPE[mention.mentionType] ?? DEFAULT_PLACEHOLDER

  return (
    <EngagementReplyForm
      reply={reply}
      channel={channel}
      textareaRef={textareaRef}
      expanded={expanded}
      canReply={canReply}
      id={`${mention.id}-reply`}
      ariaLabel={`Reply to mention from ${
        mention.author?.username ?? 'this account'
      }`}
      processingTestId="mention-item"
      placeholder={placeholder}
      assets={mention.assets}
      cta="publish-mentions-replyForm-newPost-1"
      suggestions={
        <MentionReplyFormHeader onSuggestionClick={reply.insertSuggestion} />
      }
      className={styles.replyForm}
    />
  )
}
